"use client";

import CTAButton from "@/src/components/cta-button";
import { cn } from "@/src/lib/shadcn-utils";

/**
 * Unselected buttons are outlined and selected ones filled, so which verdict was recorded is
 * readable from the button itself and not only from the card's colour — the footer sits below the
 * fold on a long card.
 *
 * Correct is drawn in `verified`, the colour the card itself turns once it's clicked, so the
 * button previews its own outcome.
 */
const VERDICT_BUTTON_CLASS = {
  correct: {
    on: "border-verified bg-verified text-verified-foreground hover:bg-verified/90",
    off: "border-verified/50 text-verified hover:border-verified hover:bg-verified/10",
  },
  incorrect: {
    on: "border-failed bg-failed text-failed-foreground hover:bg-failed/90",
    off: "border-failed/50 text-failed hover:border-failed hover:bg-failed/10",
  },
} as const;

export function VerdictButton({
  label,
  tone,
  selected,
  disabled,
  focusOnCommandEnter,
  onClick,
}: {
  label: string;
  tone: keyof typeof VERDICT_BUTTON_CLASS;
  selected: boolean;
  disabled: boolean;
  focusOnCommandEnter?: boolean;
  onClick: () => void;
}) {
  const { on, off } = VERDICT_BUTTON_CLASS[tone];

  return (
    <CTAButton
      type="button"
      variant="outline"
      small
      aria-label={`Mark this step ${label.toLowerCase()}`}
      aria-pressed={selected}
      disabled={disabled}
      focusOnCommandEnter={focusOnCommandEnter}
      onClick={onClick}
      // `h-8` matches `Button size="sm"`, which every continuation control on the page already
      // uses, so the card footer reads as one set of controls rather than two.
      className={cn("h-8 flex-1 border", selected ? on : off)}
    >
      {label}
    </CTAButton>
  );
}
